import { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config/api";

// Use centralized API config that auto-detects environment
const API = API_URL;

// Constants
const TOKEN_VERIFY_TIMEOUT_MS = 5000;
const HTTP_UNAUTHORIZED = 401;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(true);

  // Token verification effect
  // Note: API, axios are module-level constants; state setters are stable
  // Only token needs to be in dependency array
  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          // Add timeout to prevent hanging
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), TOKEN_VERIFY_TIMEOUT_MS);
          
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          });
          clearTimeout(timeoutId);
          setUser(response.data);
        } catch (error) {
          // Token verification failed - clear invalid token
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };

    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    // Add timeout to login request
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password }, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      const { access_token, user: userData } = response.data;
      localStorage.setItem("token", access_token);
      setToken(access_token);
      setUser(userData);
      return userData;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError' || error.code === 'ERR_CANCELED') {
        throw new Error("Login timeout - server reageert niet. Probeer het opnieuw.");
      }
      throw error;
    }
  };

  const register = async (email, password, companyName) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      company_name: companyName
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem("token", access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem("token");
    setToken(null);
    setUser(null);
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        // User refresh failed - if unauthorized, token may be invalid
        if (error.response?.status === HTTP_UNAUTHORIZED) {
          localStorage.removeItem("token");
          setToken(null);
          setUser(null);
        }
      }
    }
  };

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${token}`
  });

  const canDownload = () => {
    if (!user) return false;
    return user.subscription_plan === "basic" || user.subscription_plan === "pro" || user.subscription_plan === "admin" || user.is_admin;
  };

  const getMaxDuration = () => {
    if (!user) return 4;
    if (user.subscription_plan === "demo" && !user.is_admin) return 4;
    return 12;
  };

  const isAdmin = () => {
    return user?.is_admin === true;
  };

  return (
    <AuthContext.Provider value={{ 
      user, token, loading, login, register, logout, 
      getAuthHeaders, refreshUser, canDownload, getMaxDuration, isAdmin 
    }}>
      {children}
    </AuthContext.Provider>
  );
};
